const API_BASE = import.meta.env.VITE_API_URL || '/api';
// For OAuth, we need to go directly to PHP server (not through Vite proxy)
// so the cookie domain matches the callback
const AUTH_BASE = import.meta.env.VITE_AUTH_URL || API_BASE;

interface FetchOptions extends RequestInit {
  params?: Record<string, string | number | boolean>;
}

// Event filter types for the messages page
export interface EventFilters {
  event_uuid?: string;
  session_id?: string;
  git_remote?: string;
  file_name?: string;
  event_type?: string;
  date_from?: string;
  date_to?: string;
  has_message?: boolean;
}

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  getToken(): string | null {
    return this.token;
  }

  private async fetch<T>(
    endpoint: string,
    options: FetchOptions = {}
  ): Promise<T> {
    const { params, ...fetchOptions } = options;

    let url = `${API_BASE}${endpoint}`;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        searchParams.append(key, String(value));
      });
      url += `?${searchParams.toString()}`;
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      credentials: 'include', // Include cookies
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Auth endpoints - use AUTH_BASE to go directly to PHP server
  getGitHubAuthUrl(): string {
    return `${AUTH_BASE}/auth/github`;
  }

  async getMe() {
    return this.fetch<{
      user: import('../types').User;
      auth_type: string;
      api_keys: import('../types').ApiKey[];
    }>('/me');
  }

  async logout() {
    return this.fetch('/auth/logout');
  }

  // Events endpoints
  async deleteAllEvents() {
    return this.fetch<{
      status: string;
      deleted_count: number;
      message: string;
    }>('/events', { method: 'DELETE' });
  }

  async getEvents(limit = 50, offset = 0, filters: EventFilters = {}, sort: 'ASC' | 'DESC' = 'DESC') {
    const params: Record<string, string | number | boolean> = { limit, offset, sort };

    // Add filter params, only if they have values
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params[key] = value;
      }
    });

    return this.fetch<{
      events: import('../types').ConversationEvent[];
      limit: number;
      offset: number;
      total: number;
      filters: Partial<EventFilters>;
    }>('/events', { params });
  }

  // Stats endpoint
  async getStats(days = 30) {
    return this.fetch<import('../types').Stats>('/stats', { params: { days } });
  }

  // Community stats (public, cached)
  async getCommunityStats() {
    return this.fetch<import('../types').CommunityStats>('/community-stats');
  }

  // Health check
  async healthCheck() {
    return this.fetch<{ status: string; timestamp: string }>('/health');
  }

  // API Keys
  async createApiKey() {
    return this.fetch<{
      status: string;
      api_key: string;
      message: string;
    }>('/api-keys', { method: 'POST' });
  }

  async revokeApiKey(id: number) {
    return this.fetch<{ status: string; message: string }>('/api-keys', {
      method: 'DELETE',
      body: JSON.stringify({ id }),
    });
  }

  // CLI Auth
  async approveCliAuth(userCode: string) {
    return this.fetch<{ status: string; message: string }>('/cli/auth/approve', {
      method: 'POST',
      body: JSON.stringify({ user_code: userCode }),
    });
  }

  // Shares
  async createShare(data: {
    scope_type: 'all' | 'message' | 'session' | 'repository' | 'conversation';
    scope_event_uuid?: string;
    scope_session_id?: string;
    scope_git_remote_url?: string;
    scope_file_name?: string;
    visibility: 'public' | 'users' | 'github_teammates' | 'private';
    title?: string;
    description?: string;
    slug?: string;
    expires_at?: string;
    granted_users?: string[];
  }) {
    return this.fetch<{
      status: string;
      share_id: number;
      share_token: string;
      share_url: string;
      slug: string | null;
    }>('/shares', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getShares(limit = 20, offset = 0) {
    return this.fetch<{
      shares: import('../types').Share[];
      total: number;
      limit: number;
      offset: number;
    }>('/shares', { params: { limit, offset } });
  }

  async getShare(id: number) {
    return this.fetch<{
      share: import('../types').Share;
      share_url: string;
      granted_users: { id: number; username: string; avatar_url: string }[];
    }>(`/shares/${id}`);
  }

  async updateShare(id: number, data: {
    title?: string;
    description?: string;
    visibility?: 'public' | 'users' | 'github_teammates' | 'private';
    expires_at?: string | null;
    is_active?: boolean;
  }) {
    return this.fetch<{ status: string }>(`/shares/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteShare(id: number) {
    return this.fetch<{ status: string }>(`/shares/${id}`, {
      method: 'DELETE',
    });
  }

  // Public share access (no auth required for public shares)
  async getSharedContent(token: string, limit = 50, offset = 0) {
    return this.fetch<{
      share: {
        title: string | null;
        description: string | null;
        scope_type: string;
        created_at: string;
        owner: {
          username: string;
          avatar_url: string | null;
        };
      };
      content: {
        events: import('../types').ConversationEvent[];
        total: number;
        session_id?: string;
        git_remote_url?: string;
        file_name?: string;
      };
      limit: number;
      offset: number;
    }>(`/s/${token}`, { params: { limit, offset } });
  }
}

export const api = new ApiClient();
export default api;
